/**
 * Calculation Builder Component
 * 
 * Visual builder for configuring property calculations using the calculation type catalog.
 * Supports same-artifact and external artifact property references.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Form, Select, Input, Card, Space, Button, Typography, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Option } = Select;
const { Text, Title } = Typography;

interface CalculationType {
  name: string;
  description: string;
  input_properties: Record<string, any>;
  output_type: string;
  config_properties?: Record<string, any>;
}

interface CalculationBuilderProps {
  form: any;
  fieldName: number | string;
  artifactId: string;
  currentProperties: Array<{ property_key: string; property_type: string }>;
  onCalculationTypeChange?: (type: string) => void;
}

export const CalculationBuilder: React.FC<CalculationBuilderProps> = ({
  form,
  fieldName,
  artifactId,
  currentProperties,
  onCalculationTypeChange
}) => {
  const [calculationTypes, setCalculationTypes] = useState<Record<string, CalculationType>>({});
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string | undefined>();
  const formRef = useRef(form);

  // Keep form ref up to date
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    fetchCalculationTypes();
  }, []);

  const fetchCalculationTypes = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/prime-artifacts/calculation-types');
      setCalculationTypes(response.data.calculation_types || {});
    } catch (error: any) {
      console.error('Error fetching calculation types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculationTypeChange = (type: string) => {
    setSelectedType(type);
    onCalculationTypeChange?.(type);
    
    // Get existing logic to preserve any existing values
    const existingLogic = form.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || 
                         (form.getFieldValue(['transformed_references', fieldName])?.transformation_logic || {});
    
    // Initialize transformation logic structure
    const calcType = calculationTypes[type];
    if (calcType) {
      const sourceProperties: Record<string, any> = existingLogic.source_properties || {};
      const calculationConfig: Record<string, any> = existingLogic.calculation_config || {};
      
      // Initialize source properties based on calculation type (only if not already set)
      Object.keys(calcType.input_properties || {}).forEach(propKey => {
        if (!sourceProperties[propKey]) {
          sourceProperties[propKey] = {
            source: 'same_artifact', // Default to same-artifact
            property_key: ''
          };
        }
      });
      
      // Initialize config properties with defaults (only if not already set)
      if (calcType.config_properties) {
        Object.keys(calcType.config_properties).forEach(configKey => {
          const configProp = calcType.config_properties![configKey];
          if (calculationConfig[configKey] === undefined) {
            calculationConfig[configKey] = configProp.default;
          }
        });
      }
      
      const updatedLogic = {
        type: type,
        source_properties: sourceProperties,
        calculation_config: calculationConfig
      };
      
      // Set both in the nested structure and at the root level for compatibility
      form.setFieldValue(['transformed_references', fieldName, 'transformation_logic'], updatedLogic);
      
      // Also ensure the ref object has transformation_logic
      const refData = form.getFieldValue(['transformed_references', fieldName]) || {};
      form.setFieldValue(['transformed_references', fieldName], {
        ...refData,
        transformation_logic: updatedLogic
      });
    }
  };

  const updateSourceProperty = (propKey: string, source: 'same_artifact' | 'external_artifact', propertyKey?: string, artifactId?: string) => {
    console.log('updateSourceProperty called:', { propKey, source, propertyKey, artifactId });
    // Get logic from nested structure or flat structure
    const refData = form.getFieldValue(['transformed_references', fieldName]) || {};
    const logic = refData.transformation_logic || form.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
    const sourceProperties = { ...(logic.source_properties || {}) };
    
    sourceProperties[propKey] = {
      source: source,
      property_key: propertyKey || ''
    };
    
    if (source === 'external_artifact' && artifactId) {
      sourceProperties[propKey].artifact_id = artifactId;
    }
    
    const updatedLogic = {
      ...logic,
      source_properties: sourceProperties
    };
    
    console.log('Updating transformation_logic:', updatedLogic);
    // Update both nested and flat structure
    form.setFieldValue(['transformed_references', fieldName, 'transformation_logic'], updatedLogic);
    form.setFieldValue(['transformed_references', fieldName], {
      ...refData,
      transformation_logic: updatedLogic
    });
    
    // Force form to notify about the change
    form.setFieldsValue({ 
      [`transformed_references_${fieldName}_update`]: Date.now() 
    });
  };

  const updateCalculationConfig = (configKey: string, value: any) => {
    // Get logic from nested structure or flat structure
    const refData = form.getFieldValue(['transformed_references', fieldName]) || {};
    const logic = refData.transformation_logic || form.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
    const calculationConfig = { ...(logic.calculation_config || {}) };
    
    calculationConfig[configKey] = value;
    
    const updatedLogic = {
      ...logic,
      calculation_config: calculationConfig
    };
    
    // Update both nested and flat structure
    form.setFieldValue(['transformed_references', fieldName, 'transformation_logic'], updatedLogic);
    form.setFieldValue(['transformed_references', fieldName], {
      ...refData,
      transformation_logic: updatedLogic
    });
  };

  // Get current transformation logic - check both nested and flat structure
  const refData = form.getFieldValue(['transformed_references', fieldName]) || {};
  const currentLogic = refData.transformation_logic || form.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
  const currentCalcType = currentLogic.type || selectedType;
  const calcType = currentCalcType ? calculationTypes[currentCalcType] : null;
  
  // Initialize selectedType from existing logic if available
  useEffect(() => {
    if (currentCalcType && !selectedType && Object.keys(calculationTypes).length > 0) {
      setSelectedType(currentCalcType);
      // Also set the calculation_type field in the form
      formRef.current.setFieldValue(['transformed_references', fieldName, 'calculation_type'], currentCalcType);
    }
  }, [currentCalcType, calculationTypes, selectedType, fieldName]);
  
  // Sync calculation_type when transformation_logic is loaded
  // Use a more specific dependency to avoid circular reference issues
  useEffect(() => {
    // Only run this effect when we have a fieldName and form is ready
    if (!fieldName) return;
    
    const refData = formRef.current.getFieldValue(['transformed_references', fieldName]) || {};
    const logic = refData.transformation_logic || formRef.current.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
    const currentCalcType = formRef.current.getFieldValue(['transformed_references', fieldName, 'calculation_type']);
    
    if (logic.type && logic.type !== currentCalcType) {
      formRef.current.setFieldValue(['transformed_references', fieldName, 'calculation_type'], logic.type);
      setSelectedType(logic.type);
    }
  }, [fieldName]); // Only depend on fieldName, not form object

  return (
    <Card size="small" style={{ marginTop: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Form.Item
          label="Calculation Type"
          name={[fieldName, 'calculation_type']}
          rules={[{ required: true, message: 'Please select a calculation type' }]}
          initialValue={currentCalcType}
        >
          <Select
            placeholder="Select calculation type"
            loading={loading}
            onChange={handleCalculationTypeChange}
            value={currentCalcType}
          >
            {Object.entries(calculationTypes).map(([typeId, typeDef]) => (
              <Option key={typeId} value={typeId}>
                <div>
                  <Text strong>{typeDef.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{typeDef.description}</Text>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {calcType && (
          <>
            <Divider orientation="left" style={{ margin: '8px 0' }}>
              Input Properties
            </Divider>
            
            {Object.entries(calcType.input_properties || {}).map(([propKey, propDef]) => {
              // Use Form.Item to watch for changes
              return (
                <Form.Item
                  key={propKey}
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => {
                    // Check both nested and flat structure
                    const prevRef = prevValues?.transformed_references?.[fieldName];
                    const currRef = currentValues?.transformed_references?.[fieldName];
                    const prevLogic = prevRef?.transformation_logic || prevValues?.transformed_references?.[fieldName]?.transformation_logic;
                    const currLogic = currRef?.transformation_logic || currentValues?.transformed_references?.[fieldName]?.transformation_logic;
                    const prevUpdate = prevValues?.[`transformed_references_${fieldName}_update`];
                    const currUpdate = currentValues?.[`transformed_references_${fieldName}_update`];
                    return prevLogic !== currLogic || prevUpdate !== currUpdate || prevRef !== currRef;
                  }}
                >
                  {({ getFieldValue }) => {
                    // Get logic from nested structure or flat structure
                    const refData = getFieldValue(['transformed_references', fieldName]) || {};
                    const logic = refData.transformation_logic || getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
                    const currentSource = logic.source_properties?.[propKey]?.source || 'same_artifact';
                    const currentPropertyKey = logic.source_properties?.[propKey]?.property_key;
                    
                    return (
                      <Card size="small" style={{ backgroundColor: '#fafafa', marginBottom: 8 }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <Text strong>{propKey}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{propDef.description}</Text>
                          
                          <Space>
                            <Select
                              value={currentSource}
                              onChange={(value) => {
                                console.log('Source changed:', value);
                                updateSourceProperty(propKey, value);
                              }}
                              style={{ width: 150 }}
                            >
                              <Option value="same_artifact">Same Artifact</Option>
                              <Option value="external_artifact">External Artifact</Option>
                            </Select>
                            
                            {currentSource === 'same_artifact' ? (
                              <Select
                                placeholder="Select property"
                                value={currentPropertyKey}
                                onChange={(value) => {
                                  console.log('Property selected:', value);
                                  updateSourceProperty(propKey, 'same_artifact', value);
                                }}
                                style={{ width: 200 }}
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                              >
                                {currentProperties.map(prop => (
                                  <Option key={prop.property_key} value={prop.property_key}>
                                    {prop.property_key} ({prop.property_type})
                                  </Option>
                                ))}
                              </Select>
                            ) : (
                        <>
                          <Select
                            placeholder="Select external artifact"
                            style={{ width: 200 }}
                            // TODO: Add external artifact selector
                          >
                            {/* External artifact selection will be added */}
                          </Select>
                          <Select
                            placeholder="Select property"
                            style={{ width: 200 }}
                            // TODO: Add external property selector
                          >
                            {/* External property selection will be added */}
                          </Select>
                            </>
                          )}
                        </Space>
                      </Space>
                    </Card>
                    );
                  }}
                </Form.Item>
              );
            })}

            {calcType.config_properties && Object.keys(calcType.config_properties).length > 0 && (
              <>
                <Divider orientation="left" style={{ margin: '8px 0' }}>
                  Configuration
                </Divider>
                
                {Object.entries(calcType.config_properties).map(([configKey, configDef]) => {
                  // Get current value from logic (check both nested and flat structure)
                  const refData = form.getFieldValue(['transformed_references', fieldName]) || {};
                  const logic = refData.transformation_logic || form.getFieldValue(['transformed_references', fieldName, 'transformation_logic']) || {};
                  const currentValue = logic.calculation_config?.[configKey] ?? configDef.default;
                  
                  return (
                    <Form.Item
                      key={configKey}
                      label={configKey}
                      tooltip={configDef.description}
                    >
                      {configDef.type === 'number' ? (
                        <Input
                          type="number"
                          value={currentValue}
                          onChange={(e) => updateCalculationConfig(configKey, parseFloat(e.target.value) || 0)}
                          placeholder={configDef.default?.toString()}
                        />
                      ) : (
                        <Input
                          value={currentValue}
                          onChange={(e) => updateCalculationConfig(configKey, e.target.value)}
                          placeholder={configDef.default?.toString()}
                        />
                      )}
                    </Form.Item>
                  );
                })}
              </>
            )}
          </>
        )}
      </Space>
    </Card>
  );
};

export default CalculationBuilder;

